import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, Form, Button, Table, Card } from 'antd';
import { generateExportFileName, toLocalTime } from 'utils';
import { getRangeDate, setRangeDate, clearRangeDate } from 'utils/dateRangeStorage';
import { FormProps } from 'rc-field-form';
import 'styles/detailed-analysis-report.scss';
import { useGetDetailedAnalysisReport } from 'hooks/useGetDetailedAnalysisReport';
import { getColumnDetailedAnalysisReport } from 'constants/table';
import { PAGE_SIZE_DETAILED_ANALYSIS_REPORT } from 'services/api/module/report.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import ExportDetailedAnalysisReport from 'components/common/export-detailed-analysis-report';
import { useTranslation } from 'react-i18next';
import IBButton from 'helper/permission.action';
import { PermissionActionTypes } from 'helper/type/action.type';
import useModal from 'stores/useModal';
import { useSelectMultiBranches } from 'hooks/useSelectMultiBranches';
import SelectMultiBranchesModal from 'components/common/select-multi-branches-modal';
import set from 'lodash/set';

const initStartDate = dayjs().startOf('day');

function DetailedAnalysisReport() {
  const { t } = useTranslation();
  const { isOpenSourceBranchesModal, setIsOpenSourceBranchesModal } = useModal();
  const [form] = Form.useForm();

  const [isInitialized, setIsInitialized] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DETAILED_ANALYSIS_REPORT);
  const [filterOptions, setFilterOptions] = useState<any>();

  const {
    selectedSourceBranches,
    setSelectedSourceBranches,
    setAppliedSourceBranches,
    hasSourceBranchesPermission,
    getBranchIdsString
  } = useSelectMultiBranches(PermissionActionTypes.DETAILED_ANALYSIS_REPORT_BRANCHES);

  const {
    data: detailedData,
    pagination,
    isFetching
  } = useGetDetailedAnalysisReport(filterOptions, page, pageSize);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_DETAILED_ANALYSIS_REPORT, page, filterOptions]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  // Initialization effect
  useEffect(() => {
    const savedRange = getRangeDate();
    if (savedRange) {
      const startDate = dayjs(savedRange.startDate);
      const endDate = dayjs(savedRange.endDate);
      form.setFieldsValue({
        rangeDate: [startDate, endDate]
      });
      setPage(1);
      setPageSize(PAGE_SIZE_DETAILED_ANALYSIS_REPORT);
      setFilterOptions({
        check_in: toLocalTime(String(startDate), 'YYYY-MM-DD'),
        check_out: toLocalTime(String(endDate), 'YYYY-MM-DD')
      });
    } else {
      form.setFieldsValue({
        rangeDate: [initStartDate, initStartDate.add(1, 'day')]
      });
      setPage(1);
      setPageSize(PAGE_SIZE_DETAILED_ANALYSIS_REPORT);
      setFilterOptions({
        check_in: toLocalTime(String(initStartDate), 'YYYY-MM-DD'),
        check_out: toLocalTime(String(initStartDate.add(1, 'day')), 'YYYY-MM-DD')
      });
    }
    setIsInitialized(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearRangeDate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        set(restFilterOptions, 'check_in', toLocalTime(String(startDate), 'YYYY-MM-DD'));
        set(restFilterOptions, 'check_out', toLocalTime(String(endDate), 'YYYY-MM-DD'));
        const dayCount = endDate.diff(startDate, 'day');
        setRangeDate(startDate, endDate, dayCount + 1);
      }
    }

    if (hasSourceBranchesPermission) {
      const branchIds = getBranchIdsString();
      if (branchIds) {
        set(restFilterOptions, 'branch_ids', branchIds);
      }
    }

    if (page !== 1) {
      setPage(1);
    }
    setFilterOptions(restFilterOptions);
  };

  const handleRangeDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const dayCount = end.diff(start, 'day');
      setRangeDate(start, end, dayCount + 1);
    }
  };

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const columnDetailedAnalysisReport = useMemo(() => {
    return getColumnDetailedAnalysisReport(t);
  }, [t]);

  if (!isInitialized) return null;

  return (
    <div className="pms-detailed-analysis-report">
      <Card title={t('managePage.detailedAnalysisReport.title')}>
        <div className="pms-detailed-analysis-report__timetable">
          <Form
            form={form}
            initialValues={{
              rangeDate: [initStartDate, initStartDate.add(1, 'day')]
            }}
            onFinish={onFinish}
            autoComplete="off"
          >
            <div className="pms-detailed-analysis-report__filter">
              <div className="pms-detailed-analysis-report__filter__left">
                <Form.Item name="rangeDate">
                  <DatePicker.RangePicker
                    className="w-full"
                    placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                    format="DD-MM-YYYY"
                    onChange={handleRangeDateChange}
                  />
                </Form.Item>
                <Form.Item>
                  <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                    {t('common.actions.submitButton')}
                  </Button>
                </Form.Item>
                <Form.Item>
                  <IBButton
                    key="ok"
                    permission={PermissionActionTypes.DETAILED_ANALYSIS_REPORT_BRANCHES}
                    className="ant-btn-secondary"
                    onClick={() => setIsOpenSourceBranchesModal(true)}
                  >
                    {t('common.selectMultipleBranches')}
                  </IBButton>
                </Form.Item>
              </div>
              <ExportDetailedAnalysisReport
                fileName={generateExportFileName('detailed-analysis-report')}
                filterOptions={filterOptions}
                t={t}
              />
            </div>
          </Form>
          <div className="pms-detailed-analysis-report__table">
            <Table
              loading={isFetching}
              rowKey="id"
              columns={columnDetailedAnalysisReport}
              dataSource={detailedData}
              bordered
              rowClassName={record => (String(record.sale_order_ref) === 'TOTAL' ? 'bold-row' : '')}
              pagination={{
                current: page,
                position: ['bottomCenter'],
                total: pagination.total,
                pageSize: pageSize,
                locale: { items_per_page: `/ ${t('common.page')}` },
                showTotal: total => `${total} ${t('common.item')}`,
                onChange: (page: number) => handleChangePage(page),
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                }
              }}
              locale={{
                emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
              }}
              scroll={{ x: 1800, y: 500 }}
            />
          </div>
        </div>
      </Card>

      <SelectMultiBranchesModal
        open={isOpenSourceBranchesModal}
        onCancel={() => setIsOpenSourceBranchesModal(false)}
        onOk={() => {
          setAppliedSourceBranches(selectedSourceBranches);
          setIsOpenSourceBranchesModal(false);
        }}
        selectedSourceBranches={selectedSourceBranches}
        setSelectedSourceBranches={setSelectedSourceBranches}
      />
    </div>
  );
}

export default DetailedAnalysisReport;
